"use client";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import {
  BookOpen,
  Check,
  CircleDollarSign,
  CircleHelp,
  Copy,
  Download,
  ExternalLink,
  Gift,
  Globe,
  Laptop,
  Lightbulb,
  LogOut,
  Mail,
  Megaphone,
  MessageCircle,
  Monitor,
  Moon,
  PhoneCall,
  Plus,
  Settings,
  Smartphone,
  Sun,
  SunMoon,
  Users,
} from "lucide-react";
import {
  DropdownMenuDivider,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@speakai/ui/dropdown-menu";
import { Avatar } from "@speakai/ui/avatar";
import { cn } from "@/lib/utils/cn";
import { PLATFORM_LANGUAGES } from "@speakai/common/src/config/platform-languages";
import { LanguageList } from "@/components/layout/language-list";
import { normalizeTheme, type Theme } from "@/components/layout/theme-toggle";
import { trackSidebarEvent } from "@/components/layout/sidebar-primitives";
import { useIntercom } from "@/hooks/use-intercom";
import { usePermissions } from "@/hooks/use-permissions";
import { useBookingCta, useBookingImpression } from "@/features/booking/use-booking-cta";
import { useTeamInvites } from "@/features/team/queries";
import { useCopyInviteLink, useTeamSeats } from "@/features/team/hooks/use-team-invite";
import { appDownloadUrl } from "@speakai/common/src/apps/app-links";
import { HELP_URL, REFERRAL_SIGNUP_URL } from "@/config/constants";
import { isAndroid, isIOS, isMac } from "@/lib/utils/ua";
import { SUGGESTION_COPY } from "@speakai/common/src/feedback/copy";
import { useSuggestChange } from "@/features/improvements/use-suggest-change";

const ICON_CLASS = "h-3.5 w-3.5 shrink-0 text-muted-foreground";

const THEME_OPTIONS: { value: Theme; icon: typeof Sun; labelKey: string; defaultLabel: string }[] = [
  { value: "light", icon: Sun, labelKey: "SIDEBAR.MENU.THEME_LIGHT", defaultLabel: "Light" },
  { value: "dark", icon: Moon, labelKey: "SIDEBAR.MENU.THEME_DARK", defaultLabel: "Dark" },
  { value: "system", icon: Monitor, labelKey: "SIDEBAR.MENU.THEME_SYSTEM", defaultLabel: "System" },
];

const APP_ROWS: {
  id: "ios_app" | "android_app" | "mac_app" | "windows_app";
  url?: string;
  icon: typeof Laptop;
  labelKey: string;
  label: string;
  hintKey: string;
  hint: string;
  note?: { key: string; text: string };
}[] = [
  { id: "ios_app", url: appDownloadUrl("ios", "app-menu"), icon: Smartphone, labelKey: "SIDEBAR.MENU.APP_IPHONE", label: "iPhone and iPad", hintKey: "SIDEBAR.MENU.APP_STORE", hint: "App Store" },
  { id: "android_app", url: appDownloadUrl("android", "app-menu"), icon: Smartphone, labelKey: "SIDEBAR.MENU.APP_ANDROID", label: "Android", hintKey: "SIDEBAR.MENU.GOOGLE_PLAY", hint: "Google Play" },
  {
    id: "mac_app",
    url: appDownloadUrl("mac", "app-menu"),
    icon: Laptop,
    labelKey: "SIDEBAR.MENU.APP_MAC",
    label: "Mac app",
    hintKey: "ACTION.DOWNLOAD",
    hint: "Download",
    note: { key: "SIDEBAR.MENU.APP_MAC_NOTE", text: "Needs a Mac with Apple silicon" },
  },
  { id: "windows_app", icon: Laptop, labelKey: "SIDEBAR.MENU.APP_WINDOWS", label: "Windows", hintKey: "AUTOMATION.LIBRARY.COMING_SOON", hint: "Coming soon" },
];

/** App rows with the visitor's own device first; Windows and other computers keep the default order. */
function appRowsForThisDevice() {
  const ownId = isMac() ? "mac_app" : isIOS() ? "ios_app" : isAndroid() ? "android_app" : null;
  if (!ownId) return APP_ROWS;
  return [...APP_ROWS.filter((row) => row.id === ownId), ...APP_ROWS.filter((row) => row.id !== ownId)];
}

interface ProfileMenuContentProps {
  displayName: string;
  userAvatar?: string;
  userEmail?: string;
  planSummary: string | null;
  isTrialActive: boolean;
  changingLanguage: string | null;
  onSelectLanguage: (code: string) => void;
  onNavigate: (href: string) => void;
  onLogout: () => void;
}

export function ProfileMenuContent({
  displayName,
  userAvatar,
  userEmail,
  planSummary,
  isTrialActive,
  changingLanguage,
  onSelectLanguage,
  onNavigate,
  onLogout,
}: ProfileMenuContentProps) {
  const { t, i18n } = useTranslation();
  const { canManageBilling, canManageTeamMembers } = usePermissions();
  const currentLang = i18n.language || "en";
  const currentLangLabel = PLATFORM_LANGUAGES.find((l) => l.code === currentLang)?.label ?? "English";
  const { show: showIntercom } = useIntercom();
  const suggestChange = useSuggestChange();

  const seats = useTeamSeats({ enabled: canManageTeamMembers });
  const { data: inviteData } = useTeamInvites({ enabled: canManageTeamMembers });
  const shareUrl = inviteData?.invite?.shareUrl;
  const { copy: copyInviteLink } = useCopyInviteLink("account_menu");

  const seatsLabel =
    canManageTeamMembers && seats.allowedMembers > 1
      ? t("SIDEBAR.MENU.SEATS", { used: seats.totalCount, total: seats.allowedMembers, defaultValue: "{{used}}/{{total}} seats" })
      : null;
  const headerLine = [planSummary, seatsLabel].filter(Boolean).join(" · ");

  useEffect(() => {
    trackSidebarEvent("Account Menu", "Open");
  }, []);

  function select(item: string, action: () => void) {
    trackSidebarEvent("Account Menu", "Item Click", item);
    action();
  }

  function openExternal(item: string, url: string) {
    select(item, () => window.open(url, "_blank", "noopener,noreferrer"));
  }

  return (
    <>
      <div className="flex items-center gap-3 px-3 py-2.5">
        <Avatar name={displayName} src={userAvatar} size="sm" imgReferrerPolicy="no-referrer" className={cn("shrink-0 ring-1 ring-border", userAvatar && "bg-white")} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm font-semibold text-foreground">{displayName}</span>
          {userEmail && <span className="truncate text-[11px] text-muted-foreground">{userEmail}</span>}
          {headerLine && (
            <span className="line-clamp-2 text-[11px] text-muted-foreground" data-testid="profile-menu-plan-line">
              {headerLine}
            </span>
          )}
        </div>
      </div>

      <DropdownMenuDivider />

      <DropdownMenuItem onClick={() => select("settings", () => onNavigate("/profile"))}>
        <Settings className={ICON_CLASS} />
        <span className="truncate">{t("MENU.SETTINGS")}</span>
      </DropdownMenuItem>

      {canManageBilling && (
        <DropdownMenuItem onClick={() => select(isTrialActive ? "upgrade_plan" : "manage_plan", () => onNavigate("/pricing"))}>
          <CircleDollarSign className={ICON_CLASS} />
          <span className="truncate">{isTrialActive ? t("ACTION.UPGRADE_PLAN") : t("PROFILE.MANAGE_PLAN")}</span>
        </DropdownMenuItem>
      )}

      <DropdownMenuSub>
        <DropdownMenuSubTrigger icon={<CircleHelp className={ICON_CLASS} />} data-testid="profile-menu-help">
          {t("SIDEBAR.MENU.HELP_SUPPORT", "Help & Support")}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent width="w-64" backLabel={t("ACTION.BACK")} aria-label={t("SIDEBAR.MENU.HELP_SUPPORT", "Help & Support")}>
          <TrackFlyoutOpen flyout="help" />
          <DropdownMenuItem onClick={() => openExternal("help_center", HELP_URL)}>
            <BookOpen className={ICON_CLASS} />
            <span className="truncate">{t("SIDEBAR.MENU.HELP_CENTER", "Help Center")}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => select("chat_with_us", showIntercom)}>
            <MessageCircle className={ICON_CLASS} />
            <span className="truncate">{t("SIDEBAR.MENU.CHAT_WITH_US", "Chat with Us")}</span>
          </DropdownMenuItem>
          {suggestChange.enabled && (
            <DropdownMenuItem onClick={() => select("suggest_change", suggestChange.open)} data-testid="profile-menu-suggest-change">
              <Lightbulb className={ICON_CLASS} />
              <span className="truncate">{t("IMPROVEMENTS.SUGGEST_CHANGE", SUGGESTION_COPY.entryLabel)}</span>
            </DropdownMenuItem>
          )}
          <BookCallMenuItem />
          <DropdownMenuItem onClick={() => select("whats_new", () => onNavigate("/changelog"))}>
            <Megaphone className={ICON_CLASS} />
            <span className="truncate">{t("MENU.WHATS_NEW")}</span>
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <DropdownMenuSub>
        <DropdownMenuSubTrigger icon={<Globe className={ICON_CLASS} />} hint={currentLangLabel} data-testid="profile-menu-language">
          {t("MENU.LANGUAGE")}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent width="w-64" backLabel={t("ACTION.BACK")} aria-label={t("MENU.LANGUAGE")}>
          <TrackFlyoutOpen flyout="language" />
          <LanguageList
            currentLang={currentLang}
            changingLanguage={changingLanguage}
            onSelectLanguage={(code) => select(`language_${code}`, () => onSelectLanguage(code))}
          />
        </DropdownMenuSubContent>
      </DropdownMenuSub>

      <ThemeSubMenu />

      <DropdownMenuDivider />

      {canManageTeamMembers && !seats.isLimitReached && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            icon={<Users className={ICON_CLASS} />}
            data-testid="profile-menu-invite"
          >
            {t("SIDEBAR.MENU.INVITE_TEAM", "Invite Team")}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent width="w-64" backLabel={t("ACTION.BACK")} aria-label={t("SIDEBAR.MENU.INVITE_TEAM", "Invite Team")}>
            <TrackFlyoutOpen flyout="invite" />
            {shareUrl && (
              <DropdownMenuItem onClick={() => void copyInviteLink(shareUrl)}>
                <Copy className={ICON_CLASS} />
                <span className="truncate">{t("SIDEBAR.MENU.COPY_INVITE_LINK", "Copy Invite Link")}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => select("invite_by_email", () => onNavigate("/team?invite=email"))}>
              <Mail className={ICON_CLASS} />
              <span className="truncate">{t("SIDEBAR.MENU.INVITE_BY_EMAIL", "Invite by Email")}</span>
            </DropdownMenuItem>
            <DropdownMenuDivider />
            <DropdownMenuItem onClick={() => select("manage_team", () => onNavigate("/team"))}>
              <Settings className={ICON_CLASS} />
              <span className="truncate">{t("SIDEBAR.MENU.MANAGE_TEAM", "Manage Team & Link")}</span>
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}
      {canManageTeamMembers && seats.isLimitReached && canManageBilling && (
        <DropdownMenuItem
          data-testid="profile-menu-add-seats"
          onClick={() => select("add_seats", () => onNavigate("/pricing"))}
        >
          <Plus className={ICON_CLASS} />
          <span className="truncate">{t("SIDEBAR.MENU.ADD_SEATS", "Add Seats")}</span>
        </DropdownMenuItem>
      )}
      <GetAppsSubMenu onOpenApp={openExternal} />
      <DropdownMenuItem onClick={() => openExternal("refer_earn", REFERRAL_SIGNUP_URL)}>
        <Gift className={ICON_CLASS} />
        <span className="flex-1 truncate">{t("SIDEBAR.MENU.REFER_EARN", "Refer & Earn")}</span>
        <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
      </DropdownMenuItem>

      <DropdownMenuDivider />

      <DropdownMenuItem variant="danger" onClick={() => select("sign_out", onLogout)}>
        <LogOut className="h-3.5 w-3.5 shrink-0" />
        <span>{t("ACTION.SIGN_OUT")}</span>
      </DropdownMenuItem>
    </>
  );
}

/** Mounts only while its flyout is open, so each opening is tracked once. */
function TrackFlyoutOpen({ flyout }: { flyout: string }) {
  useEffect(() => {
    trackSidebarEvent("Account Menu", "Flyout Open", flyout);
  }, [flyout]);
  return null;
}

/** Rendered only while the Help flyout is open, so the booking impression counts only when the item is visible. */
function BookCallMenuItem() {
  const { t } = useTranslation();
  const bookingCta = useBookingCta("help_menu");
  useBookingImpression(bookingCta, "help_menu", bookingCta.qualified);

  if (!bookingCta.qualified) return null;

  return (
    <DropdownMenuItem
      onClick={() => {
        bookingCta.trackClick();
        window.open(bookingCta.href, "_blank", "noopener,noreferrer");
      }}
    >
      <PhoneCall className={ICON_CLASS} />
      <span className="truncate">{t(bookingCta.label.key, { defaultValue: bookingCta.label.text })}</span>
    </DropdownMenuItem>
  );
}

function GetAppsSubMenu({ onOpenApp }: { onOpenApp: (item: string, url: string) => void }) {
  const { t } = useTranslation();
  const label = t("SIDEBAR.MENU.GET_APPS", "Get the apps");

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger icon={<Download className={ICON_CLASS} />} data-testid="profile-menu-apps">
        {label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent width="w-72" backLabel={t("ACTION.BACK")} aria-label={label}>
        <TrackFlyoutOpen flyout="apps" />
        {appRowsForThisDevice().map(({ id, url, icon: Icon, labelKey, label: rowLabel, hintKey, hint, note }) => (
          <DropdownMenuItem key={id} disabled={!url} onClick={url ? () => onOpenApp(id, url) : undefined}>
            <Icon className={ICON_CLASS} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate">{t(labelKey, rowLabel)}</span>
              {note && <span className="truncate text-[11px] text-muted-foreground">{t(note.key, note.text)}</span>}
            </span>
            <span className="shrink-0 rounded-full border border-border px-2 text-[11px] text-muted-foreground">{t(hintKey, hint)}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

function ThemeSubMenu() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const currentTheme = normalizeTheme(theme);
  const current = THEME_OPTIONS.find((option) => option.value === currentTheme) ?? THEME_OPTIONS[0];

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        icon={<SunMoon className={ICON_CLASS} />}
        hint={t(current.labelKey, current.defaultLabel)}
        data-testid="profile-menu-theme"
      >
        {t("SIDEBAR.MENU.THEME", "Theme")}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent width="w-64" backLabel={t("ACTION.BACK")} aria-label={t("SIDEBAR.MENU.THEME", "Theme")}>
        <TrackFlyoutOpen flyout="theme" />
        {THEME_OPTIONS.map(({ value, icon: Icon, labelKey, defaultLabel }) => {
          const isCurrent = current.value === value;
          return (
            <DropdownMenuItem
              key={value}
              closeOnSelect={false}
              aria-current={isCurrent ? "true" : undefined}
              onClick={() => {
                trackSidebarEvent("Account Menu", "Item Click", `theme_${value}`);
                setTheme(value);
              }}
            >
              <Icon className={ICON_CLASS} />
              <span className="flex-1 truncate">{t(labelKey, defaultLabel)}</span>
              {isCurrent && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
